/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Translations Selectors
 *
 * @author Yakov Gushchin
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.0.0
 */

import { createSelector } from 'reselect';
import { OutputSelector } from 'reselect/src';

import { IFastActionsTranslation } from '@/main/components/FastActions/types/fastActionsTypes';
import { IWizardAdditionalReportsTranslation } from '@modules/internationalization/types/wizards/additionalReports';
import { IWizardChangeRoleTranslation } from '@modules/internationalization/types/wizards/changeRole';
import type { IRootReducer } from '@reducers/rootReducer';

import { initTranslation } from './constants';
import type { IIntState } from './intReducer';
import { initialIntState } from './intReducer';
import type {
    ICommonTranslation,
    ICreateInquiryTranslation,
    IEdmOperatorConnectionTranslation,
    IUserNotificationTranslation,
    IOnboardingTranslation,
    IPageAuthServiceAccessTranslation,
    IPageAuthTranslation,
    IPageAutoReportIdTranslation,
    IPageExpensesAnalyzerTranslation,
    IPageHistoryInquiryTranslation,
    IPageHistoryOrderTranslation,
    IPageOrderIdTranslation,
    IPagePaymentsTranslation,
    IPagePersonalAccountTranslation,
    IPageSubscriberMobileTranslation,
    IPersonalDataConfirmTranslation,
    IPersonalDataDeleteTranslation,
    ITranslation,
    IWizardBillsDocTranslation,
    IWizardMultiSearchTranslation,
    IWizardProductOfferingsTranslation,
    IWizardImportSubscribersLimitsTranslation,
    IPageNewsTranslation,
} from './types';
import type { IFormAddressTranslation } from './types/modules/formAddress';
import type { IPromisedPaymentTranslation } from './types/modules/promisedPayments';
import type IPageBillsTranslation from './types/pages/bills';
import type { IPageCampaignInfoTranslation } from './types/pages/campaignInfo';
import type { IPageCampaignsTranslation } from './types/pages/campaigns';
import type { IPageCashbackTranslation } from './types/pages/cashback';
import type IPageDashboardTranslation from './types/pages/dashboard/dashboard';
import type { IWidgetReportsConstructorTranslation } from './types/pages/dashboard/widgetReportsConstructor';
import type { IPageInquiryInfoTranslation } from './types/pages/inquiryInfo';
import type { IPageAccountInfoTranslation } from './types/pages/pageAccountInfoTranslation';
import type {
    IPageSettingsAutoReportsTableTranslation,
    IPageSettingsAutoReportsTranslation,
    IPageSettingsStatisticsTranslation,
    IPageSettingsTranslation,
} from './types/pages/pageSettings';
import type { IPageProductsTranslation } from './types/pages/products';
import type { IPageReportsConstructorTranslation } from './types/pages/reportsConstructor';
import type IPageSubscriberFixedTranslation from './types/pages/subscriberFixed';
import type { IPageSubscriberIdTranslation } from './types/pages/subscriberId';
import type { IWizardAdditionalAccountsTranslation } from './types/wizards/additionalAccounts';
import type { ICallsDetailTranslation } from './types/wizards/callsDetail';
import type { IWizardChangeLockTranslation } from './types/wizards/changeLock';
import type { IWizardCollationReportTranslation } from './types/wizards/collationReport';
import type { IWizardExpensesDetailTranslation } from './types/wizards/expensesDetail';
import type { IWizardImportPersonalDataTranslation } from './types/wizards/importPersonalData';
import type { ILabelsImportTranslation } from './types/wizards/labelsImport';
import type { IMoneyTransferTranslation } from './types/wizards/moneyTransfer';
import type { IWizardOrderReportTranslation } from './types/wizards/orderReport';
import type { IWizardOrderTemplateTranslation } from './types/wizards/orderTemplate';
import type { IWizardTranslation } from './types/wizards/wizard';


/**
 * To state from the global state of internationalization
 * @param state {IRootReducer} - global state
 * @returns {IIntState} internationalization state
 */
const selectInt = (state: IRootReducer): IIntState => state?.internationalization ?? initialIntState;

/**
 * Receive translation
 * @returns {OutputSelector<IRootReducer, ITranslation, {}>}
 */
export const makeSelectTranslation = (): OutputSelector<
    IRootReducer,
    ITranslation,
    (state: IIntState) => ITranslation
> => createSelector(selectInt, (state: IIntState) => state.translation ?? initTranslation);

export const makeSelectTranslationCommon = (): OutputSelector<
    IRootReducer,
    ICommonTranslation,
    (state: IIntState) => ICommonTranslation
> =>
    createSelector(selectInt, (state: IIntState) => ({
        ...(state.translation.common ?? initTranslation.common),
        ...(state.translation.corporate ?? initTranslation.corporate),
    }));

export const makeSelectTranslationPageAuth = (): OutputSelector<
    IRootReducer,
    IPageAuthTranslation,
    (state: IIntState) => IPageAuthTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageAuth);

export const makeSelectTranslationPageAuthServiceAccess = (): OutputSelector<
    IRootReducer,
    IPageAuthServiceAccessTranslation,
    (state: IIntState) => IPageAuthServiceAccessTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageAuthServiceAccess);

export const makeSelectTranslationPageDashboard = (): OutputSelector<
    IRootReducer,
    IPageDashboardTranslation,
    (state: IIntState) => IPageDashboardTranslation
> => createSelector(selectInt, (state) => state.translation.pageDashboard);

export const makeSelectTranslationPageAnalyzer = (): OutputSelector<
    IRootReducer,
    IPageExpensesAnalyzerTranslation,
    (state: IIntState) => IPageExpensesAnalyzerTranslation
> => createSelector(selectInt, (state) => state.translation.pageExpensesAnalyzer);

export const makeSelectWizardTranslation = (): OutputSelector<
    IRootReducer,
    IWizardTranslation,
    (state: IIntState) => IWizardTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizard);

export const makeSelectMoneyTransferTranslation = (): OutputSelector<
    IRootReducer,
    IMoneyTransferTranslation,
    (state: IIntState) => IMoneyTransferTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.moneyTransfer);

export const makeSelectLabelsImportTranslation = (): OutputSelector<
    IRootReducer,
    ILabelsImportTranslation,
    (state: IIntState) => ILabelsImportTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.labelsImport);

export const makeSelectCallsDetailTranslation = (): OutputSelector<
    IRootReducer,
    ICallsDetailTranslation,
    (state: IIntState) => ICallsDetailTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.callsDetail);

export const makeSelectTranslationPageSettings = (): OutputSelector<
    IRootReducer,
    IPageSettingsTranslation,
    (state: IIntState) => IPageSettingsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageSettings);

export const makeSelectTranslationPageProducts = (): OutputSelector<
    IRootReducer,
    IPageProductsTranslation,
    (state: IIntState) => IPageProductsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageProducts);

export const makeSelectTranslationWizardProductOfferings = (): OutputSelector<
    IRootReducer,
    IWizardProductOfferingsTranslation,
    (state: IIntState) => IWizardProductOfferingsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardProductOfferings);

export const makeSelectTranslationWizardExpensesDetail = (): OutputSelector<
    IRootReducer,
    IWizardExpensesDetailTranslation,
    (state: IIntState) => IWizardExpensesDetailTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardExpensesDetail);

export const makeSelectTranslationWizardBillsDoc = (): OutputSelector<
    IRootReducer,
    IWizardBillsDocTranslation,
    (state: IIntState) => IWizardBillsDocTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardBillsDoc);

export const makeSelectPageSettingsAutoReportsTranslation = (): OutputSelector<
    IRootReducer,
    IPageSettingsAutoReportsTranslation,
    (state: IIntState) => IPageSettingsAutoReportsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageSettings.settingsAutoReports);

export const makeSelectPageSettingsAutoReportsTableTranslation = (): OutputSelector<
    IRootReducer,
    IPageSettingsAutoReportsTableTranslation,
    (state: IIntState) => IPageSettingsAutoReportsTableTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageSettings.settingsAutoReports.table);

export const makeSelectPageSettingsStatisticsTranslation = (): OutputSelector<
    IRootReducer,
    IPageSettingsStatisticsTranslation,
    (state: IIntState) => IPageSettingsStatisticsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.pageSettings.settingsStatistics);

export const makeSelectTranslationWizardAdditionalAccounts = (): OutputSelector<
    IRootReducer,
    IWizardAdditionalAccountsTranslation,
    (state: IIntState) => IWizardAdditionalAccountsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardAdditionalAccounts);

export const makeSelectTranslationWizardAdditionalReports = (): OutputSelector<
    IRootReducer,
    IWizardAdditionalReportsTranslation,
    (state: IIntState) => IWizardAdditionalReportsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardAdditionalReports);

export const makeSelectTranslationWizardChangeLock = (): OutputSelector<
    IRootReducer,
    IWizardChangeLockTranslation,
    (state: IIntState) => IWizardChangeLockTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardChangeLock);

export const makeSelectTranslationWizardImportPersonalData = (): OutputSelector<
    IRootReducer,
    IWizardImportPersonalDataTranslation,
    (state: IIntState) => IWizardImportPersonalDataTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardImportPersonalData);

export const makeSelectTranslationWizardMultiSearch = (): OutputSelector<
    IRootReducer,
    IWizardMultiSearchTranslation,
    (state: IIntState) => IWizardMultiSearchTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardMultiSearch);

export const makeSelectTranslationWizardImportSubscribersLimits = (): OutputSelector<
    IRootReducer,
    IWizardImportSubscribersLimitsTranslation,
    (state: IIntState) => IWizardImportSubscribersLimitsTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardImportSubscribersLimits);

export const makeSelectTranslationWizardOrderTemplate = (): OutputSelector<
    IRootReducer,
    IWizardOrderTemplateTranslation,
    (state: IIntState) => IWizardOrderTemplateTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardOrderTemplate);

export const makeSelectTranslationWizardOrderReport = (): OutputSelector<
    IRootReducer,
    IWizardOrderReportTranslation,
    (state: IIntState) => IWizardOrderReportTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardOrderReport);

export const makeSelectTranslationWizardCollationReport = (): OutputSelector<
    IRootReducer,
    IWizardCollationReportTranslation,
    (state: IIntState) => IWizardCollationReportTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardCollationReport);

export const makeSelectTranslationWizardChangeRole = (): OutputSelector<
    IRootReducer,
    IWizardChangeRoleTranslation,
    (state: IIntState) => IWizardChangeRoleTranslation
> => createSelector(selectInt, (state: IIntState) => state.translation.wizardChangeRole);

/**
 * Receive locale
 * @returns {OutputSelector<IRootReducer, ITranslation, {}>}
 */
const makeSelectLocale = (): OutputSelector<IRootReducer, string, (res: IIntState) => string> =>
    createSelector(selectInt, (state: IIntState) => state?.locale ?? initialIntState.locale);

const makeSelectCustomConfig = (): OutputSelector<
    IRootReducer,
    { [key: string]: string },
    (state: IIntState) => { [key: string]: string }
> => createSelector(selectInt, (state: IIntState) => state.custom);

export const makeSelectTranslationPageBills = (): OutputSelector<
    IRootReducer,
    IPageBillsTranslation,
    (state: IIntState) => IPageBillsTranslation
> => createSelector(selectInt, (state) => state.translation.pageBills);

export const makeSelectTranslationPagePersonalAccount = (): OutputSelector<
    IRootReducer,
    IPagePersonalAccountTranslation,
    (state: IIntState) => IPagePersonalAccountTranslation
> => createSelector(selectInt, (state) => state.translation.pagePersonalAccount);

export const makeSelectTranslationPageSubscriberMobile = (): OutputSelector<
    IRootReducer,
    IPageSubscriberMobileTranslation,
    (state: IIntState) => IPageSubscriberMobileTranslation
> => createSelector(selectInt, (state) => state.translation.pageSubscriberMobile);

export const makeSelectTranslationPageSubscriberFixed = (): OutputSelector<
    IRootReducer,
    IPageSubscriberFixedTranslation,
    (state: IIntState) => IPageSubscriberFixedTranslation
> => createSelector(selectInt, (state) => state.translation.pageSubscriberFixed);

export const makeSelectPageSubscriberIdTranslation = (): OutputSelector<
    IRootReducer,
    IPageSubscriberIdTranslation,
    (state: IIntState) => IPageSubscriberIdTranslation
> => createSelector(selectInt, (state) => state.translation.pageSubscriberId);

export const makeSelectPromisedPaymentTranslation = (): OutputSelector<
    IRootReducer,
    IPromisedPaymentTranslation,
    (state: IIntState) => IPromisedPaymentTranslation
> => createSelector(selectInt, (state) => state.translation.promisedPayment);

export const makeSelectPageAccountInfoTranslation = (): OutputSelector<
    IRootReducer,
    IPageAccountInfoTranslation,
    (state: IIntState) => IPageAccountInfoTranslation
> => createSelector(selectInt, (state) => state.translation.pageAccountInfo);

export const makeSelectPageCashbackTranslation = (): OutputSelector<
    IRootReducer,
    IPageCashbackTranslation,
    (state: IIntState) => IPageCashbackTranslation
> => createSelector(selectInt, (state) => state.translation.pageCashback);

export const makeSelectPageCampaignsTranslation = (): OutputSelector<
    IRootReducer,
    IPageCampaignsTranslation,
    (state: IIntState) => IPageCampaignsTranslation
> => createSelector(selectInt, (state) => state.translation.pageCampaigns);

export const makeSelectPageCampaignInfoTranslation = (): OutputSelector<
    IRootReducer,
    IPageCampaignInfoTranslation,
    (state: IIntState) => IPageCampaignInfoTranslation
> => createSelector(selectInt, (state) => state.translation.pageCampaignInfo);

export const makeSelectFormAddressTranslation = (): OutputSelector<
    IRootReducer,
    IFormAddressTranslation,
    (state: IIntState) => IFormAddressTranslation
> => createSelector(selectInt, (state) => state.translation.formAddress);

export const makeSelectPagePaymentsTranslation = (): OutputSelector<
    IRootReducer,
    IPagePaymentsTranslation,
    (state: IIntState) => IPagePaymentsTranslation
> => createSelector(selectInt, (state) => state.translation.pagePayments);

export const makeSelectPageInquiryInfoTranslation = (): OutputSelector<
    IRootReducer,
    IPageInquiryInfoTranslation,
    (state: IIntState) => IPageInquiryInfoTranslation
> => createSelector(selectInt, (state) => state.translation.pageInquiryInfo);

export const makeSelectPageHistoryInquiryTranslation = (): OutputSelector<
    IRootReducer,
    IPageHistoryInquiryTranslation,
    (state: IIntState) => IPageHistoryInquiryTranslation
> => createSelector(selectInt, (state) => state.translation.pageHistoryInquiry);

export const makeSelectPageOrderTranslation = (): OutputSelector<
    IRootReducer,
    IPageHistoryOrderTranslation,
    (state: IIntState) => IPageHistoryOrderTranslation
> => createSelector(selectInt, (state) => state.translation.pageHistoryOrder);

export const makeSelectPageOrderIdTranslation = (): OutputSelector<
    IRootReducer,
    IPageOrderIdTranslation,
    (state: IIntState) => IPageOrderIdTranslation
> => createSelector(selectInt, (state) => state.translation.pageOrderId);

export const makeSelectOnboardingTranslation = (): OutputSelector<
    IRootReducer,
    IOnboardingTranslation,
    (state: IIntState) => IOnboardingTranslation
> => createSelector(selectInt, (state) => state.translation.onboarding);

export const makeSelectPersonalDataConfirmTranslation = (): OutputSelector<
    IRootReducer,
    IPersonalDataConfirmTranslation,
    (state: IIntState) => IPersonalDataConfirmTranslation
> => createSelector(selectInt, (state) => state.translation.personalDataConfirm);

export const makeSelectPersonalDataDeleteTranslation = (): OutputSelector<
    IRootReducer,
    IPersonalDataDeleteTranslation,
    (state: IIntState) => IPersonalDataDeleteTranslation
> => createSelector(selectInt, (state) => state.translation.personalDataDelete);

export const makeSelectPageAutoReportIdTranslation = (): OutputSelector<
    IRootReducer,
    IPageAutoReportIdTranslation,
    (state: IIntState) => IPageAutoReportIdTranslation
> => createSelector(selectInt, (state) => state.translation.pageAutoReportId);

export const makeSelectCreateInquiryTranslation = (): OutputSelector<
    IRootReducer,
    ICreateInquiryTranslation,
    (state: IIntState) => ICreateInquiryTranslation
> => createSelector(selectInt, (state) => state.translation.createInquiry);

export const makeSelectEdmOperatorConnectionTranslation = (): OutputSelector<
    IRootReducer,
    IEdmOperatorConnectionTranslation,
    (state: IIntState) => IEdmOperatorConnectionTranslation
> => createSelector(selectInt, (state) => state.translation.edmOperatorConnection);

export const makeSelectPageNewsTranslation = (): OutputSelector<
    IRootReducer,
    IPageNewsTranslation,
    (state: IIntState) => IPageNewsTranslation
> => createSelector(selectInt, (state) => state.translation.pageNews);

export const makeSelectPageReportsConstructorTranslation = (): OutputSelector<
    IRootReducer,
    IPageReportsConstructorTranslation,
    (state: IIntState) => IPageReportsConstructorTranslation
> => createSelector(selectInt, (state) => state.translation.pageReportsConstructor);

export const makeSelectWidgetReportsConstractorTranslation = (): OutputSelector<
    IRootReducer,
    IWidgetReportsConstructorTranslation,
    (state: IIntState) => IWidgetReportsConstructorTranslation
> => createSelector(selectInt, (state) => state.translation.pageDashboard.widgets.reportsConstructor);

export const makeSelectUserNotificationsTranslation = (): OutputSelector<
    IRootReducer,
    IUserNotificationTranslation,
    (state: IIntState) => IUserNotificationTranslation
> => createSelector(selectInt, (state) => state.translation.userNotifications);

export const makeSelectTranslationFastActions = (): OutputSelector<
    IRootReducer,
    IFastActionsTranslation,
    (state: IIntState) => IFastActionsTranslation
> => createSelector(selectInt, (state) => state.translation.fastActions);

export { makeSelectCustomConfig, makeSelectLocale, selectInt };
